import React, { useEffect, useState } from "react";

import { makeStyles, Button, Icon, CircularProgress } from "@material-ui/core";

import CreateSubscriptionModal from "../../../components/Modals/CreateSubscriptionModal/CreateSubscriptionModal";
import CongratulationModal from "../../../components/Modals/CongratulationModal/CongratulationModal";
import SubscriptionCreatingErrorModal from "../../../components/Modals/SubscriptionCreatingErrorModal/SubscriptionCreatingErrorModal";
import UpdateHostsQuantityTable from "../../../components/UpdateHostsQuantityTable/UpdateHostsQuantityTable";
import { setupFee } from "../../../utils/constants";
import { apiErrorMessage } from "../../../helpers/apiErrorMessage";
import "../Subscription.css";

const useStyles = makeStyles({
  button: {
    maxWidth: 220,
    display: "flex",
    margin: "30px auto",
  },
  backButtonOuter: {
    display: "flex",
    justifyContent: "center",
  },
  backButton: {
    marginTop: 100,
  },
  paymentMethods: {
    display: "flex",
    gap: "1rem",
    justifyContent: "center",
    marginTop: "1.5rem",
    flexWrap: "wrap",
  },
  paymentButton: {
    minWidth: 200,
    padding: "12px 24px",
    borderRadius: 8,
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.875rem",
  },
  stripeButton: {
    background: "#635BFF",
    color: "#fff",
    "&:hover": {
      background: "#4B45C6",
    },
  },
  paypalButton: {
    background: "#FFC439",
    color: "#253B80",
    "&:hover": {
      background: "#F0B72D",
    },
  },
  stripeProcessing: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "1rem",
    padding: "2rem",
    textAlign: "center",
  },
});

const CreateSubscription = ({
  subscriptionStatus,
  subscriptionEnd,
  subscriptionQuantity,
  createAdminSubscription,
  createStripeCheckout,
  pollStripeSession,
  getPrice,
  back,
}) => {
  const [hostsQuantity, setHostsQuantity] = useState(3);
  const [quantityError, setQuantityError] = useState("");
  const [openCongratulationModal, setOpenCongratulationModal] = useState(false);
  const [subscriptionCreated, setSubscriptionCreated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [approved, setApproved] = useState(false);
  const [openCreateSubscriptionModal, setOpenCreateSubscriptionModal] =
    useState(false);
  const [openSubscriptionErrorModal, setOpenSubscriptionErrorModal] =
    useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");

  const classes = useStyles();

  const handleClose = () => {
    setOpenCreateSubscriptionModal(false);
    setOpenSubscriptionErrorModal(false);
    setCheckoutError("");
  };

  const closeCongratulationModal = () => {
    setOpenCongratulationModal(false);
    back();
  };

  const decreaseHostsQuantity = () => {
    if (hostsQuantity <= 3) {
      setQuantityError("Minimum hosts quantity is: 3");
      return;
    }
    setQuantityError("");
    setHostsQuantity(hostsQuantity - 1);
  };

  const increaseHostsQuantity = () => {
    setHostsQuantity(hostsQuantity + 1);
    setQuantityError("");
  };

  const paypalSubscribe = (data, actions) => {
    return actions.subscription.create({
      plan_id: process.env.REACT_APP_PAYPAL_PLAN_ID,
      quantity: hostsQuantity,
    });
  };

  const paypalOnError = (err) => {
    setCheckoutError(
      apiErrorMessage(err, "PayPal couldn't start this subscription. Try again, or pay with a card.")
    );
    setOpenSubscriptionErrorModal(true);
    setOpenCreateSubscriptionModal(false);
  };

  const paypalOnApprove = (data, detail) => {
    setApproved(true);
    createSubscription(data.subscriptionID);
  };

  const createSubscription = async (subscriptionID) => {
    try {
      setLoading(true);
      await createAdminSubscription(subscriptionID);
      setLoading(false);
      setSubscriptionCreated(true);
    } catch (err) {
      setLoading(false);
      paypalOnError(err);
    }
  };

  const handleStripeCheckout = async () => {
    try {
      setStripeLoading(true);
      const response = await createStripeCheckout(hostsQuantity);
      if (response.url) {
        window.location.href = response.url;
      } else {
        setStripeLoading(false);
        setCheckoutError("Stripe didn't return a checkout link. Try again.");
        setOpenSubscriptionErrorModal(true);
      }
    } catch (err) {
      console.error("Stripe checkout error:", JSON.stringify(err.message || err));
      setStripeLoading(false);
      setCheckoutError(
        apiErrorMessage(
          err,
          "Couldn't start card checkout. Try again, or pay with PayPal."
        )
      );
      setOpenSubscriptionErrorModal(true);
    }
  };

  useEffect(() => {
    if (subscriptionCreated && subscriptionStatus === "ACTIVE") {
      setOpenCreateSubscriptionModal(false);
      setOpenCongratulationModal(true);
    }
  }, [subscriptionStatus, subscriptionCreated]);

  const monthlyPrice = getPrice(hostsQuantity);
  const dueToday = monthlyPrice + setupFee;

  return (
    <>
      <h1>Create Subscription</h1>

      <UpdateHostsQuantityTable
        error={quantityError}
        hostsQuantity={hostsQuantity}
        increase={increaseHostsQuantity}
        decrease={decreaseHostsQuantity}
      />

      <div className="subscription-order-summary">
        <div className="subscription-section">
          <div className="subscription-section-header">
            <Icon className="subscription-section-icon">receipt</Icon>
            <h2 className="subscription-section-title">Order summary</h2>
          </div>

          <div className="subscription-details-grid">
            <div className="subscription-detail-row">
              <span className="subscription-detail-label">
                Hosts subscription
                <span className="subscription-order-item-meta">
                  {hostsQuantity} host{hostsQuantity === 1 ? "" : "s"} · billed monthly
                </span>
              </span>
              <span className="subscription-detail-value price">
                ${monthlyPrice}
              </span>
            </div>

            <div className="subscription-detail-row">
              <span className="subscription-detail-label">
                Setup fee
                <span className="subscription-order-item-meta">
                  One-time down payment
                </span>
              </span>
              <span className="subscription-detail-value price">
                ${setupFee}
              </span>
            </div>

            <div className="subscription-detail-row order-total">
              <span className="subscription-detail-label">Due today</span>
              <span className="subscription-detail-value">${dueToday}</span>
            </div>

            <div className="subscription-detail-row order-recurring">
              <span className="subscription-detail-label">
                Then monthly
                <span className="subscription-order-item-meta">
                  Starting next billing cycle
                </span>
              </span>
              <span className="subscription-detail-value price">
                ${monthlyPrice}/mo
              </span>
            </div>
          </div>
        </div>
      </div>

      {stripeLoading ? (
        <div className={classes.stripeProcessing}>
          <CircularProgress size={36} style={{ color: "#635BFF" }} />
          <p style={{ color: "#5D6D7E", margin: 0 }}>
            Redirecting to Stripe checkout...
          </p>
        </div>
      ) : (
        <div className={classes.paymentMethods}>
          <Button
            variant="contained"
            className={`${classes.paymentButton} ${classes.stripeButton}`}
            onClick={handleStripeCheckout}
            startIcon={<Icon>credit_card</Icon>}
          >
            Pay with Card
          </Button>
          <Button
            variant="contained"
            className={`${classes.paymentButton} ${classes.paypalButton}`}
            onClick={() => setOpenCreateSubscriptionModal(true)}
            startIcon={<Icon>account_balance</Icon>}
          >
            Pay with PayPal
          </Button>
        </div>
      )}

      <div className={classes.backButtonOuter}>
        <Button
          className={classes.backButton}
          variant="contained"
          color="default"
          onClick={back}
        >
          Go back
        </Button>
      </div>

      <CreateSubscriptionModal
        open={openCreateSubscriptionModal}
        hostsQuantity={hostsQuantity}
        loading={loading}
        approved={approved}
        price={getPrice(hostsQuantity)}
        handleClose={handleClose}
        paypalSubscribe={paypalSubscribe}
        onApprove={paypalOnApprove}
        catchError={paypalOnError}
        onError={paypalOnError}
      />
      <CongratulationModal
        open={openCongratulationModal}
        handleClose={closeCongratulationModal}
        subscriptionStatus={subscriptionStatus}
        subscriptionEnd={subscriptionEnd}
        subscriptionQuantity={subscriptionQuantity}
      >
        Your subscription is now <b>{subscriptionStatus}</b> till{" "}
        <b>{subscriptionEnd && subscriptionEnd.slice(0, 10)}</b>, and you can
        add <b>{subscriptionQuantity}</b> hosts!
      </CongratulationModal>
      <SubscriptionCreatingErrorModal
        open={openSubscriptionErrorModal}
        message={checkoutError}
        handleClose={handleClose}
      />
    </>
  );
};

export default CreateSubscription;
